import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, Save, CheckCircle, Users, Lock, LockOpen, CalendarDays, CalendarRange, LayoutGrid, Clock, ChevronLeft, ChevronRight, Target, Eraser, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  DURUMLAR, DURUM_SINIF, DURUM_KOD, DURUM_ETIKET,
  PUANTAJ_VARSAYILAN,
  ayarlariBirlestir, hesapla, sayi,
  ilaveHakHesapla, kadrodaMi, donemleri, isoGun,
} from '@/lib/puantaj';

// TAHAKKUK KORUMASI — ekranda içinde bulunulan AYDAN İLERİ gidilemez.
//
// Gün durumu girilmemişse varsayılan "çalıştı" sayılıyor (bkz. `etkinDurum` ve
// `hr.py` → `_puantaj_hesapla`), yani hiç doldurulmamış bir ay için tahakkuk
// çalıştırmak tüm kadroya 30 gün tam maaş yazıyor. 2026-09-12'de `2031/9`
// dönemine bu yolla 26 personel / 1.720.605 TL sahte borç oluştu (silindi).
//
// İKİ AYRI SINIR:
//  - GEZİNME ay bazında: gelecek aya geçilemez (`ayIleride`); aylık ızgara
//    içinde bulunulan ayın tamamını gösterir.
//  - GİRİŞ gün bazında (kullanıcı, 2026-09-28): bugünden sonraki günler
//    görünür ama kilitli — henüz yaşanmamış güne durum, mesai ya da geçici
//    görev yazılamaz. Departman sorumlusunun girişsiz bağlantısı
//    (`puantaj_linki.py` → `_tarih_kontrol`) bu kuralı zaten uyguluyordu; bu
//    ekran onunla aynı hale getirildi. Önceden ileri tarihe girilmiş hatalı bir
//    kaydı düzeltmenin yolu "Temizle"dir; silme bilerek serbest bırakıldı.
// Bu sınır yalnızca ekranda. Sunucu ileri tarihli durumu reddetmiyor: aylık
// kaydet tüm ayın haritasını gönderiyor ve eskiden girilmiş ileri tarihli bir
// kayıt, sunucu reddetseydi o ayın HER kaydını engellerdi.
// Bir ISO günden n gün sonrası. Gezinme düğmelerinin varış noktasını sınıra
// sormak için: düğme, gideceği gün sınırı aşıyorsa kapanır.
const ileriGun = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};
const ayBugun = () => { const d = new Date(); return { yil: d.getFullYear(), ay: d.getMonth() + 1 }; };
const ayIleride = (yil, ay) => {
  const s = ayBugun();
  return yil > s.yil || (yil === s.yil && ay > s.ay);
};
// Gün/hafta gezinmesinin ve girişin üst sınırı: BUGÜN (yerel gün).
const sonSecilebilirGun = () => new Date().toLocaleDateString('en-CA');
const gunIleride = (iso) => !!iso && iso > sonSecilebilirGun();
// Haftanın pazartesisi (hafta Pzt başlar, bkz. HaftalikGorunum).
const haftaBasi = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toLocaleDateString('en-CA');
};

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const HAFTA = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

const bugun = () => new Date().toLocaleDateString('en-CA');
// Net Maaş sütun genişliğini tabloya yazar; Özet bu kadar sola sabitlenir
// (bkz. Timesheet → netBaslikRef).
const netGenislikYaz = (th) => th.closest('table')?.style.setProperty(
  '--net-maas-w', `${th.getBoundingClientRect().width}px`);
// hr.py ILAVE_HAK_TURLERI ile aynı kodlar (yalnızca gösterim)
const HAK_ADI = { yol: 'Yol', yemek: 'Yemek', telefon: 'Telefon', gider_destegi: 'Gider Desteği', kira_destegi: 'Kira Desteği' };
const paraBicim = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });

// Bir günün hücresi: rozet + tıklayınca durum listesi ve o güne ait mesai.
// Aylık ızgara ve haftalık görünüm aynı bileşeni kullanır.
function DurumHucresi({ durum, saat, kilitli, soluk, baslik,
                        onDurum, onMesai, testId }) {

  const rozet = (
    <div className={`relative w-7 h-7 rounded flex items-center justify-center
                     text-white text-[10px] font-bold
                     ${DURUM_SINIF[durum] || 'bg-gray-400'} ${soluk ? 'opacity-40' : ''}`}>
      {DURUM_KOD[durum]}
      {/* Negatif saat = EKSIK calisma; kirmizi rozetle ayirt ediliyor */}
      {!!saat && (
        <span className={`absolute -top-1 -right-1 text-white rounded-full
                         px-1 text-[8px] leading-[12px] font-semibold
                         ${saat < 0 ? 'bg-[#a34b3f]' : 'bg-[#1a1916]'}`}>
          {saat < 0 ? '−' : ''}{Math.abs(saat) % 1 === 0 ? Math.abs(saat) : Math.abs(saat).toFixed(1)}
        </span>
      )}
    </div>
  );

  if (kilitli) return rozet;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId}
          title={`${baslik} — ${DURUM_ETIKET[durum]}${saat ? ` · ${saat > 0 ? `${saat} sa fazla mesai` : `${Math.abs(saat)} sa eksik calisma`}` : ''}`}>
          {rozet}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2 bg-[#faf7f2] border-[#d0c4b0]" align="start">
        <p className="text-xs font-semibold text-[#8a7e70] px-1 pb-1.5">{baslik}</p>
        <div className="space-y-0.5">
          {DURUMLAR.map(d => (
            <button key={d.deger} type="button" onClick={() => onDurum(d.deger)}
              className={`w-full flex items-center gap-2 px-1.5 py-1 rounded text-left text-xs
                          hover:bg-[#e8dfd0] ${durum === d.deger ? 'bg-[#e8dfd0] font-semibold' : ''}`}
              data-testid={`puantaj-durum-${d.deger}`}>
              <span className={`w-5 h-5 rounded ${d.sinif} text-white text-[9px] font-bold
                                flex items-center justify-center shrink-0`}>{d.kod}</span>
              <span className="text-[#1a1916]">{d.etiket}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-[#d0c4b0] mt-2 pt-2 px-1">
          <label className="text-[11px] font-medium text-[#8a7e70] flex items-center gap-1">
            <Clock size={11} /> Mesai (saat)
          </label>
          {/* İKİ YÖNLÜ: pozitif fazla mesai, negatif eksik çalışma (ör. -3) */}
          <Input type="number" step="0.5" value={saat || ''} placeholder="0"
            onChange={(e) => onMesai(e.target.value)}
            className="h-7 mt-1 bg-white border-[#d0c4b0] text-xs" />
        </div>
      </PopoverContent>
    </Popover>
  );
}


export default function Timesheet() {
  const simdi = new Date();
  // Tahakkuk (onay) ayrı yetki; sunucu da `timesheet/approve` istiyor.
  const { hasPermission } = useAuth();
  const tahakkukYetkisi = hasPermission('timesheet', 'approve');
  const [gorunum, setGorunum] = useState('ay');            // 'ay' | 'hafta' | 'gun'
  const [year, setYear] = useState(simdi.getFullYear());
  const [month, setMonth] = useState(simdi.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [departmanlar, setDepartmanlar] = useState([]);
  const [departmanId, setDepartmanId] = useState('hepsi');   // 'hepsi' | departman id | 'yok'
  const [temizlePencere, setTemizlePencere] = useState(false);
  const [temizleAralik, setTemizleAralik] = useState({ bas: 1, bit: 31 });
  const [temizliyor, setTemizliyor] = useState(false);
  const [entries, setEntries] = useState({});
  const [mesai, setMesai] = useState({});                  // {empId: {gun: saat}}
  // Sorumluluk merkezi SAPMALARI: {empId: {gun: merkezId}}. Yalnızca personelin
  // asıl merkezinden (employees.cost_center_id) farklı çalıştığı günler yazılır;
  // boş gün "asıl merkez" demek. Tahakkuk bu dağılıma göre merkezlere bölünüyor.
  const [merkezler, setMerkezler] = useState({});
  const [merkezKatalogu, setMerkezKatalogu] = useState([]);
  const [gorevDialog, setGorevDialog] = useState(null);    // düzenlenen personel
  const [ayar, setAyar] = useState(PUANTAJ_VARSAYILAN);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [isApproved, setIsApproved] = useState(false);
  const [accruals, setAccruals] = useState({});
  const [accruingId, setAccruingId] = useState(null);
  // TAHAKKUK KİLİDİ (kullanıcı, 2026-09-22). Tahakkuk edilmiş personelin günü
  // sessizce değiştirilirse cariye yazılmış tutarla puantaj birbirini tutmaz.
  // Liste SUNUCUDAN, cari defterden TÜRETİLEREK gelir — ekranda saklanan bir
  // bayrak, kayıt elle silindiğinde asılı kalırdı.
  const [tahakkukKalemleri, setTahakkukKalemleri] = useState({});
  const [kalemAdlari, setKalemAdlari] = useState({});
  // Kilidi açmak OTURUMLUK: sayfa yenilenince kilit geri gelir, çünkü kalıcı
  // olsaydı "açık unutulmuş" bir satır tahakkukla sessizce ayrışırdı.
  const [kilitAcik, setKilitAcik] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  // SÜRÜM KİLİDİ: bu ekran ayın TAMAMINI yazıyor. Ekran açıkken izin onaylanır
  // ya da günlük/haftalık girişten biri kaydederse, eski haliyle kaydetmek o
  // günleri sessizce silerdi. Yüklenen sürüm geri gönderilir; sunucu doküman
  // o arada değiştiyse 409 döner. Ref, çünkü kaydet → tahakkuk zincirinde
  // state güncellemesini beklemeden yeni sürüm kullanılmalı.
  const surumRef = useRef(0);

  useEffect(() => {
    api.get('/hr/departments').then(r => setDepartmanlar(r.data || [])).catch(() => {});
  }, []);

  // Departman süzgeci TEK yerde uygulanıyor: aşağıdaki bütün türetmeler (aylık/
  // günlük listeler, toplamlar) bu listeden besleniyor. Kaydetme `entries`
  // durumunun TAMAMINI gönderdiği için süzmek veri kaybettirmez.
  const gorunenPersonel = useMemo(() => {
    if (departmanId === 'hepsi') return employees;
    if (departmanId === 'yok') return employees.filter(e => !e.department_id);
    return employees.filter(e => e.department_id === departmanId);
  }, [employees, departmanId]);

  const puantajiKaydet = async () => {
    try {
      const r = await api.post('/hr/timesheet',
        { year, month, entries, mesai, merkezler, surum: surumRef.current });
      if (r.data?.surum != null) surumRef.current = r.data.surum;
      return true;
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data?.detail || 'Puantaj siz ekrandayken değişti', { duration: 8000 });
        await fetchTimesheet();   // güncel hali göster; kullanıcı değişikliğini yeniden yapar
        return false;
      }
      throw err;
    }
  };

  const fetchTimesheet = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/hr/timesheet?year=${year}&month=${month}`);
      setEmployees(res.data.employees || []);
      setEntries(res.data.entries || {});
      setMesai(res.data.mesai || {});
      setMerkezler(res.data.merkezler || {});
      setMerkezKatalogu(res.data.merkez_katalogu || []);
      setDaysInMonth(res.data.days_in_month || 30);
      setIsApproved(res.data.is_approved || false);
      setAccruals(res.data.individual_accruals || {});
      setTahakkukKalemleri(res.data.tahakkuk_kalemleri || {});
      setKalemAdlari(res.data.tahakkuk_kalem_adlari || {});
      setAyar(ayarlariBirlestir(res.data.ayarlar));
      surumRef.current = res.data.surum ?? 0;
    } catch {
      toast.error('Puantaj yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchTimesheet(); }, [fetchTimesheet]);

  // KADRO DIŞI GÜN = devamsızlık. Kural HİZMET DÖNEMLERİNDEN okunur
  // (`kadrodaMi`), `hire_date`/`termination_date` ikilisinden DEĞİL: ayrılıp
  // geri dönen personelde `hire_date` ilk giriştir ve son dönem açıksa
  // `termination_date` boştur — iki dönem arasındaki günler "kadroda" görünür
  // ve boş hücre varsayılanı yüzünden "çalıştı" sayılırdı (Carmel Kiriza,
  // 2026-09-21: 7–31 Ağustos ve 1–14 Eylül tam çalışılmış görünüyordu).
  const etkinDurum = useCallback((emp, day) => {
    if (!kadrodaMi(emp, isoGun(year, month, day))) return 'absent';
    return entries[emp.id]?.[String(day)] ?? entries[emp.id]?.[day] ?? 'worked';
  }, [entries, year, month]);

  // Hücre kilitli/soluk gösterilsin mi: o gün kadroda değilse evet
  const kadroDisi = useCallback(
    (emp, day) => !kadrodaMi(emp, isoGun(year, month, day)),
    [year, month]);

  // Bu ayda girişe açık son gün numarası (bkz. dosya başı, GİRİŞ sınırı).
  // Her çizimde hesaplanıyor, useMemo DEĞİL: sayfa gece yarısını açık geçerse
  // ertesi gün kendiliğinden açılsın.
  const sonGirisGunu = (() => {
    if (ayIleride(year, month)) return 0;
    const s = ayBugun();
    return (year === s.yil && month === s.ay) ? new Date().getDate() : daysInMonth;
  })();
  const ileriGunMu = (day) => day > sonGirisGunu;

  // Özet ve Net Maaş ikisi de sağa sabit. İkisi `right-0` iken tablo yatayda
  // taşınca aynı noktaya yığılıyor, Net Maaş Özet'i örtüyordu. Özet, Net Maaş
  // sütununun GERÇEK genişliği kadar sola sabitlenir: o genişlik veriye bağlı
  // (ilave hak satırları, döküm, tahakkuk düğmesi), sabit bir `right-24` bir
  // satırda tutar öbüründe taşardı. Genişlik tabloya CSS değişkeni olarak
  // yazılıyor — Aylık ve Günlük personel ızgaraları ayrı tablo, ayrı genişlik.
  // Kesirli genişlik (getBoundingClientRect): offsetWidth yuvarladığı için
  // yarım piksel boşluk ya da bindirme bırakıyordu.
  // İlk ölçüm eşzamanlı da yapılıyor: ResizeObserver yalnızca sayfa ÇİZİLİRKEN
  // bildirir, arka plan sekmesinde açılan sayfada hiç tetiklenmiyor (ölçüldü).
  const netGozlemci = useRef(null);
  useEffect(() => () => netGozlemci.current?.disconnect(), []);
  const netBaslikRef = useCallback((th) => {
    if (!th) return;
    if (!netGozlemci.current) {
      netGozlemci.current = new ResizeObserver((girdiler) => {
        girdiler.forEach(({ target }) => {
          if (!target.isConnected) { netGozlemci.current.unobserve(target); return; }
          netGenislikYaz(target);
        });
      });
    }
    netGenislikYaz(th);
    netGozlemci.current.observe(th);
  }, []);

  // Anlık hesap — hücre değiştikçe otomatik güncellenir.
  // Para yazan uçlarda sunucu kendi hesabını kullanır (bkz. lib/puantaj.js).
  const hesaplar = useMemo(() => {
    const sonuc = {};
    employees.forEach(emp => {
      sonuc[emp.id] = hesapla({
        mesaiGunleri: mesai[emp.id] || {},
        aylikMaas: emp.salary || 0,
        employmentType: emp.employment_type,
        gunSayisi: daysInMonth,
        ayar,
        yil: year, ay: month,
        durumCoz: (gun) => etkinDurum(emp, gun),
        girilmisMi: (gun) => (entries[emp.id] || {})[String(gun)] !== undefined,
      });
    });
    return sonuc;
  }, [employees, entries, mesai, daysInMonth, ayar, etkinDurum, year, month]);

  // İlave haklar: {empId: [{tur, tutar, para_birimi, hesaplanan, odenen_gun}]}
  const hakHesaplari = useMemo(() => {
    const sonuc = {};
    employees.forEach(emp => {
      sonuc[emp.id] = (emp.ilave_haklar || []).map(hak => ({
        ...hak,
        ...(() => { const h = ilaveHakHesapla({ hak, durumCoz: (gun) => etkinDurum(emp, gun), gunSayisi: daysInMonth });
                    return { hesaplanan: h.tutar, odenen_gun: h.odenen_gun }; })(),
      }));
    });
    return sonuc;
  }, [employees, daysInMonth, etkinDurum]);

  const durumDegistir = (empId, day, durum) => {
    if (isApproved) return;
    setEntries(p => ({ ...p, [empId]: { ...(p[empId] || {}), [String(day)]: durum } }));
  };


  const mesaiDegistir = (empId, day, saat) => {
    if (isApproved) return;
    const v = Math.max(0, sayi(saat));
    setMesai(p => {
      const gunler = { ...(p[empId] || {}) };
      if (v > 0) gunler[String(day)] = v; else delete gunler[String(day)];
      const yeni = { ...p };
      if (Object.keys(gunler).length) yeni[empId] = gunler; else delete yeni[empId];
      return yeni;
    });
  };

  const kaydet = async () => {
    setSaving(true);
    try {
      if (!(await puantajiKaydet())) return;
      toast.success('Puantaj kaydedildi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const onayla = async () => {
    if (!window.confirm('Puantajı onaylamak istediğinize emin misiniz? Onaylanan puantaj değiştirilemez.\n\n'
      + 'Cari hesaplara maaş, fazla çalışma ve ilave haklar AYRI satırlar olarak yazılır.')) return;
    setApproving(true);
    try {
      if (!(await puantajiKaydet())) return;
      await api.post('/hr/timesheet/approve', { year, month });
      toast.success('Puantaj onaylandı');
      await fetchTimesheet();   // tahakkuk edilen tutar sunucudan gelsin
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onaylama hatası');
    } finally {
      setApproving(false);
    }
  };

  // Bir personelin tahakkuk edilmiş kalemleri ve satırın kilit durumu.
  const tahakkuklu = useCallback(
    (empId) => (tahakkukKalemleri[empId] || []), [tahakkukKalemleri]);
  const satirKilitli = useCallback(
    (empId) => tahakkuklu(empId).length > 0 && !kilitAcik[empId],
    [tahakkuklu, kilitAcik]);

  /** Kalem dökümü — bölme kuralı SUNUCUYLA birebir (`_accrue_salary_ledger`):
   *  fark üzerinden, eksik çalışma kesintisi bazı aşarsa kalan mesaiden düşer. */
  const kalemDokumu = useCallback((emp) => {
    const h = hesaplar[emp.id] || {};
    const hakToplam = (hakHesaplari[emp.id] || []).filter(x => x.hesaplanan > 0);
    const pb = emp.salary_currency || 'TRY';
    // Capa MAAS satiri (sunucuyla birebir): artik mesai tarafinda kalir,
    // yoksa "maasim 55.000 ama 54.999,99 yaziyor" oluyordu.
    let maas = h.salary_pay ?? ((h.base_pay || 0) - (h.missing_deduction || 0));
    let mesai = Math.round(((h.net_salary || 0) - maas) * 100) / 100;
    if (maas < 0) { mesai = Math.round((mesai + maas) * 100) / 100; maas = 0; }
    return {
      maas: { tutar: maas, etiket: `maaş ${paraBicim(maas)} ${pb}` },
      mesai: { tutar: mesai, etiket: `fazla çalışma ${paraBicim(mesai)} ${pb}` },
      ilave_hak: {
        tutar: hakToplam.reduce((t, x) => t + x.hesaplanan, 0),
        etiket: hakToplam.map(x => `${HAK_ADI[x.tur]} ${paraBicim(x.hesaplanan)} ${x.para_birimi}`).join(' + '),
      },
      pb,
    };
  }, [hesaplar, hakHesaplari]);

  // Tek personel tahakkuku — ör. ay ortasında işten çıkan personel için.
  // `kalemler` boşsa TÜMÜ; her kalem için ayrı düğme koymak satırı şişirirdi,
  // tek açılır menüde seçiliyor (kullanıcı, 2026-09-22).
  const tahakkukEt = async (emp, kalemler = null) => {
    const h = hesaplar[emp.id];
    const hakToplam = (hakHesaplari[emp.id] || []).filter(x => x.hesaplanan > 0);
    if (!h || !(h.net_salary > 0 || hakToplam.length)) {
      toast.error('Net maaş 0 — tahakkuk oluşturulamaz');
      return;
    }
    const d = kalemDokumu(emp);
    const secili = kalemler || ['maas', 'mesai', 'ilave_hak'];
    const dokum = secili.map(k => (d[k]?.tutar > 0 ? d[k].etiket : null)).filter(Boolean);
    if (!dokum.length) {
      toast.error('Seçilen kalemde tahakkuk edilecek tutar yok');
      return;
    }
    if (!window.confirm(`${emp.full_name} için ${dokum.join(' + ')} tahakkuku oluşturulsun mu?`
      + ` Her kalem cari hesabına AYRI borç kaydı olarak yazılacak.`
      + (kalemler ? ' Seçilmeyen kalemlerin mevcut kaydına DOKUNULMAZ.' : '')
      + (d.pb !== 'TRY' ? ' TL karşılığı TCMB döviz alış kuruyla hesaplanır.' : ''))) return;
    setAccruingId(emp.id);
    try {
      if (!(await puantajiKaydet())) return;
      await api.post('/hr/timesheet/accrue-employee',
        { year, month, employee_id: emp.id, ...(kalemler ? { kalemler } : {}) });
      toast.success(`${emp.full_name} için tahakkuk oluşturuldu`);
      // Kilit sunucudan türetiliyor: yeniden yükle ki satır kilitlensin.
      setKilitAcik(p => ({ ...p, [emp.id]: false }));
      await fetchTimesheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tahakkuk hatası');
    } finally {
      setAccruingId(null);
    }
  };

  const { aylikPersonel, gunlukPersonel } = useMemo(() => {
    const gunler = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const aktif = gorunenPersonel.filter(emp => gunler.some(g => etkinDurum(emp, g) !== 'absent'));
    return {
      aylikPersonel: aktif.filter(e => e.employment_type === 'full_time'),
      gunlukPersonel: aktif.filter(e => e.employment_type !== 'full_time'),
    };
  }, [gorunenPersonel, daysInMonth, etkinDurum]);

  // Personelin ay içindeki merkez dağılımı — satırda özet göstermek için.
  const girisleriTemizle = async () => {
    const liste = [...aylikPersonel, ...gunlukPersonel];
    const bas = Math.max(1, Math.min(daysInMonth, Number(temizleAralik.bas) || 1));
    const bit = Math.min(daysInMonth, Math.max(bas, Number(temizleAralik.bit) || daysInMonth));
    const gunler = Array.from({ length: bit - bas + 1 }, (_, i) => bas + i);
    if (!liste.length) { toast.error('Seçili listede personel yok'); return; }
    if (!window.confirm(
      `${liste.length} personelin ${bas}–${bit} arası girişleri SİLİNECEK.\n\n` +
      'Bu günler "girilmemiş" hale döner; sorumlu bağlantıdan yeniden girebilir. ' +
      'Girilmemiş gün tahakkukta ÇALIŞILDI sayılır — tahakkuktan önce yeniden doldurulmalı.\n\nDevam edilsin mi?'
    )) return;
    setTemizliyor(true);
    try {
      const r = await api.post('/hr/timesheet/temizle', {
        year, month, employee_ids: liste.map(e => e.id), gunler });
      toast.success(`${r.data.temizlenen} gün girişi temizlendi`);
      setTemizlePencere(false);
      await fetchTimesheet();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Temizlenemedi');
    } finally { setTemizliyor(false); }
  };

  const merkezOzeti = useCallback((emp) => {
    const asilId = emp.cost_center_id || null;
    const asil = merkezKatalogu.find(m => m.id === asilId);
    const sapmalar = merkezler[emp.id] || {};
    const sayac = {};
    Object.entries(sapmalar).forEach(([gun, mid]) => {
      const g = parseInt(gun, 10);
      if (!mid || !(g >= 1 && g <= daysInMonth)) return;
      sayac[mid] = (sayac[mid] || 0) + 1;
    });
    const gecici = Object.entries(sayac)
      .filter(([mid]) => mid !== asilId)
      .map(([mid, gun]) => ({
        gun,
        ad: merkezKatalogu.find(m => m.id === mid)?.name || 'Bilinmeyen merkez',
      }));
    return { asilAd: asil?.name || null, gecici };
  }, [merkezler, merkezKatalogu, daysInMonth]);

  // Geçici görev bir ARALIK olarak giriliyor (30 günün her birine ayrı seçici
  // koymak satırı okunmaz hale getirirdi); aralık burada gün gün açılıyor.
  // Aralık bugünle kırpılır (GİRİŞ sınırı): "1–30" girilse de ileri günlere yazılmaz.
  const gorevYaz = (empId, merkezId, baslangic, bitis) => {
    const son = Math.min(daysInMonth, sonGirisGunu);
    const b = Math.max(1, Math.min(daysInMonth, parseInt(baslangic, 10) || 1));
    if (b > son) { toast.error('İleri tarihli günlere görev yazılamaz'); return; }
    const s2 = Math.max(b, Math.min(son, parseInt(bitis, 10) || b));
    setMerkezler(p => {
      const gunler = { ...(p[empId] || {}) };
      for (let g = b; g <= s2; g++) {
        if (merkezId) gunler[String(g)] = merkezId;
        else delete gunler[String(g)];
      }
      return { ...p, [empId]: gunler };
    });
  };

  const gorevTemizle = (empId) => setMerkezler(p => ({ ...p, [empId]: {} }));

  // Gelecek yıl listede YOK (bkz. dosya başındaki tahakkuk koruması).
  const yillar = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y];
  }, []);

  // Yıl değiştirilince ay taşabiliyor: 2025/Aralık seçiliyken 2026'ya geçmek
  // gelecek bir aya düşerdi. Ay, izin verilen son aya çekilir.
  const yilDegistir = (yeniYil) => {
    setYear(yeniYil);
    if (ayIleride(yeniYil, month)) setMonth(ayBugun().ay);
  };

  // Para tutarları PARA BİRİMİNE GÖRE toplanır: maaş TL dışında da tanımlanabiliyor
  // ve farklı para birimlerini tek sayıda toplamak anlamsız bir rakam üretirdi.
  const toplam = useMemo(() => {
    const a = { net: {}, kesinti: {}, mesaiTutar: {}, haklar: {}, mesaiSaat: 0, rtc: 0,
                eksikSaat: 0, eksikTutar: {} };
    const ekle = (harita, pb, n) => { if (n) harita[pb] = (harita[pb] || 0) + n; };
    employees.forEach(emp => {
      const c = hesaplar[emp.id];
      if (!c) return;
      const pb = emp.salary_currency || 'TRY';
      ekle(a.net, pb, c.net_salary || 0);
      ekle(a.kesinti, pb, c.deduction || 0);
      ekle(a.mesaiTutar, pb, c.overtime_pay || 0);
      (hakHesaplari[emp.id] || []).forEach(h => ekle(a.haklar, h.para_birimi || 'TRY', h.hesaplanan));
      a.mesaiSaat += c.overtime_hours || 0;
      a.eksikSaat += c.missing_hours || 0;
      ekle(a.eksikTutar, pb, c.missing_deduction || 0);
      a.rtc += c.holiday_work_days || 0;
    });
    return a;
  }, [hesaplar, employees, hakHesaplari]);
  const pbToplam = (harita) => {
    const satirlar = Object.entries(harita).sort(([x], [y]) => (x === 'TRY' ? -1 : y === 'TRY' ? 1 : x.localeCompare(y)));
    return satirlar.length ? satirlar.map(([pb, n]) => `${paraBicim(n)} ${pb}`).join(' + ') : `${paraBicim(0)} TRY`;
  };

  // ── Bir gün hücresi: durum seçimi + o güne ait ekstra mesai ──
  // BİLEŞEN DEĞİL, düz fonksiyon: `<GunHucresi />` olarak burada tanımlanınca her
  // çizimde YENİ bir bileşen tipi oluyordu ve sayfa her yeniden çizildiğinde
  // 800+ hücrenin hepsi sıfırdan kuruluyordu. Açık durum listesi de onlarla
  // birlikte kapanıyordu ("seçenekler açılıp anında kapanıyor", 2026-09-17).
  // Açık/kapalı durumu dosya düzeyindeki `DurumHucresi` tutar, o sabit kalmalı.
  // Gün ayırıcı çizgi kenarlık değil iç gölge: başlık hücreleri sticky ve
  // `border-collapse`ta kenarlık hücreyle birlikte kaymıyor; gövdede de aynı
  // yöntem kullanılıyor ki başlık ile gövde çizgisi aynı pikselde dursun.
  // 1. gün çizgisiz: solunda Personel sütununun kenarlığı zaten var.
  const gunHucresi = (emp, day) => (
    <TableCell key={day} className={`p-0.5 text-center ${day > 1 ? 'shadow-[inset_1px_0_0_#e3d8c6]' : ''}`}>
      <DurumHucresi
        durum={etkinDurum(emp, day)}
        saat={sayi(mesai[emp.id]?.[String(day)])}
        kilitli={isApproved || kadroDisi(emp, day) || satirKilitli(emp.id) || ileriGunMu(day)}
        soluk={kadroDisi(emp, day) || satirKilitli(emp.id) || ileriGunMu(day)}
        baslik={`${emp.full_name} · ${day} ${AYLAR[month - 1]}`}
        onDurum={(d) => durumDegistir(emp.id, day, d)}
        onMesai={(v) => mesaiDegistir(emp.id, day, v)}
        testId={`puantaj-hucre-${emp.id}-${day}`}
      />
    </TableCell>
  );

  const personelSatiri = (emp) => {
    const h = hesaplar[emp.id] || {};
    return (
      <TableRow key={emp.id} className="hover:bg-[#f5f0e8]">
        <TableCell className="sticky left-0 bg-[#faf7f2] z-10 min-w-[180px] border-r border-[#d0c4b0]">
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-[#1a1916]">{emp.full_name}</p>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${emp.is_active ? 'bg-[#3d6e4e]/15 text-[#3d6e4e]' : 'bg-[#7a3535]/15 text-[#7a3535]'}`}>
              {emp.is_active ? 'Aktif' : 'Pasif'}
            </span>
          </div>
          <p className="text-xs text-[#8a7e70]">
            {emp.employment_type === 'full_time' ? 'Aylık' : 'Günlük'} | {(emp.salary || 0).toLocaleString('tr-TR')} {emp.salary_currency || 'TRY'}
          </p>
          {/* Merkez satırı yalnızca BİRDEN FAZLA merkez tanımlıysa görünür:
              tek merkezli şirkette her personelin yanında aynı adı tekrarlamak
              puantaj tablosunu bilgi vermeden kalabalıklaştırırdı. */}
          {merkezKatalogu.length > 1 && (() => {
            const oz = merkezOzeti(emp);
            return (
              <button type="button"
                onClick={() => !isApproved && !satirKilitli(emp.id) && setGorevDialog(emp)}
                disabled={isApproved || satirKilitli(emp.id)}
                title={isApproved ? 'Onaylanmış puantaj değiştirilemez'
                  : satirKilitli(emp.id) ? 'Tahakkuk edilmiş — önce kilidi açın'
                  : 'Geçici görev tanımla'}
                className={`mt-0.5 flex items-center gap-1 text-[10px] ${isApproved || satirKilitli(emp.id) ? 'text-[#8a7e70] cursor-default' : 'text-[#3d6e4e] hover:underline'}`}
                data-testid={`puantaj-merkez-${emp.id}`}>
                <Target size={10} />
                <span className="truncate max-w-[130px]">
                  {oz.asilAd || 'Merkez atanmamış'}
                </span>
                {oz.gecici.map(g => (
                  <span key={g.ad} className="px-1 rounded bg-[#2e6fa0]/15 text-[#2e6fa0] font-semibold whitespace-nowrap">
                    {g.gun}g {g.ad}
                  </span>
                ))}
              </button>
            );
          })()}
          {/* Çok dönemli personelde tek giriş/çıkış yanıltıcı: dönemleri yaz */}
          <p className="text-[10px] text-[#8a7e70] mt-0.5">
            {donemleri(emp).length > 1
              ? donemleri(emp).map(d => `${d.giris} → ${d.cikis || '…'}`).join('  ·  ')
              : `Giriş: ${emp.hire_date || '—'}${emp.termination_date ? ` · Çıkış: ${emp.termination_date}` : ''}`}
          </p>
        </TableCell>

        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
          gunHucresi(emp, day)
        ))}

        <TableCell className="sticky right-[var(--net-maas-w,6rem)] bg-[#faf7f2] z-10 border-l border-[#d0c4b0] text-center">
          <div className="text-xs space-y-0.5 whitespace-nowrap">
            <p><span className="text-[#8a7e70]">Ç:</span> <span className="font-semibold">{h.worked_days || 0}</span></p>
            <p><span className="text-[#8a7e70]">İ:</span> <span className="font-semibold">{h.paid_leave_days || 0}</span></p>
            <p><span className="text-[#8a7e70]">K:</span> <span className="font-semibold text-red-600">{(h.unpaid_days || 0) + (h.absent_days || 0)}</span></p>
            {h.holiday_work_days > 0 && (
              <p><span className="text-[#8a7e70]">RTÇ:</span> <span className="font-semibold text-cyan-800">{h.holiday_work_days}</span></p>
            )}
            {h.overtime_hours > 0 && (
              <p><span className="text-[#8a7e70]">FM:</span> <span className="font-semibold">{h.overtime_hours} sa</span></p>
            )}
          </div>
        </TableCell>

        <TableCell className="sticky right-0 bg-[#e8dfd0] z-10 text-right font-mono font-bold text-[#1a1916]">
          <div>{paraBicim(h.net_salary)}{(emp.salary_currency || 'TRY') !== 'TRY' && <span className="text-[10px] font-sans text-[#8a7e70]"> {emp.salary_currency}</span>}</div>
          {(hakHesaplari[emp.id] || []).filter(x => x.hesaplanan > 0).map(x => (
            <div key={x.tur} className="text-[9px] font-sans font-normal text-[#2e6fa0] leading-tight"
              title={x.hesaplama === 'gunluk' ? `${x.odenen_gun} çalışılan gün × ${x.tutar}` : `aylık ${x.tutar}, ${x.odenen_gun} gün ödendi`}>
              + {HAK_ADI[x.tur] || x.tur} {paraBicim(x.hesaplanan)}{(x.para_birimi || 'TRY') !== 'TRY' ? ` ${x.para_birimi}` : ''}
            </div>
          ))}
          {(h.holiday_pay > 0 || h.overtime_pay > 0) && (
            <div className="text-[9px] font-sans font-normal text-[#8a7e70] leading-tight mt-0.5">
              {paraBicim(h.base_pay)} baz
              {h.holiday_pay > 0 && <> + {paraBicim(h.holiday_pay)} RTÇ</>}
              {h.overtime_pay > 0 && <> + {paraBicim(h.overtime_pay)} mesai</>}
            </div>
          )}
          {!isApproved && tahakkukYetkisi && (() => {
            const d = kalemDokumu(emp);
            const edilen = tahakkuklu(emp.id);
            // Menüde yalnızca TUTARI OLAN kalem çıkar: 0 TL'lik bir seçeneği
            // tıklatıp "tutar yok" hatası vermek kullanıcıyı oyalardı.
            const secenekler = ['maas', 'mesai', 'ilave_hak'].filter(k => d[k].tutar > 0);
            return (
              <div className="mt-1 space-y-0.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" disabled={accruingId === emp.id}
                      className="h-6 px-2 text-[10px] font-sans border-[#d0c4b0]"
                      data-testid={`accrue-employee-${emp.id}`}>
                      {accruingId === emp.id ? <Loader2 size={10} className="animate-spin" />
                        : <>{edilen.length ? 'Tekrar Tahakkuk' : 'Tahakkuk Et'}
                            <ChevronDown size={10} className="ml-0.5" /></>}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-[11px]">Ne tahakkuk edilsin?</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => tahakkukEt(emp)}
                      data-testid={`accrue-tumu-${emp.id}`}>
                      <span className="font-semibold">Tümü</span>
                    </DropdownMenuItem>
                    {secenekler.length > 1 && <DropdownMenuSeparator />}
                    {secenekler.length > 1 && secenekler.map(k => (
                      <DropdownMenuItem key={k} onClick={() => tahakkukEt(emp, [k])}
                        data-testid={`accrue-${k}-${emp.id}`}>
                        <span className="flex-1">{kalemAdlari[k] || k}</span>
                        <span className="ml-2 font-mono text-[10px] text-[#8a7e70]">
                          {paraBicim(d[k].tutar)}
                        </span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {edilen.length > 0 && (
                  <div className="space-y-0.5">
                    <p className="text-[9px] text-[#3d6e4e] font-sans font-medium leading-tight">
                      <CheckCircle size={9} className="inline mr-0.5" />
                      {edilen.map(k => kalemAdlari[k] || k).join(' · ')} tahakkuk edildi
                    </p>
                    {/* Kilidi yalnızca tahakkuk yetkilisi açar: satır cariye
                        yazılmış tutarla eşleşiyor, değiştiren tekrar tahakkuk
                        etmek zorunda. */}
                    <button type="button"
                      onClick={() => {
                        if (!kilitAcik[emp.id] && !window.confirm(
                          `${emp.full_name} için ${year}/${month} tahakkuk edildi.\n\n`
                          + 'Kilidi açarsanız günleri değiştirebilirsiniz ama cari hesaba '
                          + 'yazılmış tutar DEĞİŞMEZ — değişiklikten sonra TEKRAR TAHAKKUK '
                          + 'etmeniz gerekir.\n\nKilit açılsın mı?')) return;
                        setKilitAcik(p => ({ ...p, [emp.id]: !p[emp.id] }));
                      }}
                      className={`text-[9px] font-sans font-medium inline-flex items-center gap-0.5
                        ${kilitAcik[emp.id] ? 'text-[#a34b3f]' : 'text-[#8a7e70] hover:text-[#1a1916]'}`}
                      data-testid={`tahakkuk-kilit-${emp.id}`}>
                      {kilitAcik[emp.id]
                        ? <><LockOpen size={9} /> Kilit açık — kapat</>
                        : <><Lock size={9} /> Kilidi aç</>}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </TableCell>
      </TableRow>
    );
  };

  const izgara = (baslik, liste) => liste.length > 0 && (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0] py-3">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Users size={18} /> {baslik} ({liste.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        {/* Uzun listede gun basliklari kaybolmasin: tablo kendi kutusunda
            dikey kayar, baslik o kutunun tepesine yapisir (bkz. ui/table.jsx
            containerClassName notu). Kose hucreleri hem ust hem yan sabit,
            bu yuzden z-30 — govdedeki sabit sutun (z-10) ve diger baslik
            hucreleri (z-20) altinda kalir. 20rem tabani: kisa ekranda
            (telefon yatay) 100vh-8rem cok kuculup izgarayi eziyordu. */}
        <Table containerClassName="max-h-[max(20rem,calc(100vh-8rem))]">
          <TableHeader>
            <TableRow className="bg-[#e8dfd0]">
              <TableHead className="sticky left-0 top-0 bg-[#e8dfd0] z-30 min-w-[180px] text-[#1a1916] font-bold border-r border-[#d0c4b0] shadow-[inset_0_-1px_0_#d0c4b0]">
                Personel
              </TableHead>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const hg = new Date(year, month - 1, day).getDay();
                const tatil = hg === 0 || hg === 6;
                return (
                  <TableHead key={day}
                    title={ileriGunMu(day) ? 'İleri tarih — henüz girilemez' : undefined}
                    className={`sticky top-0 z-20 bg-[#e8dfd0] ${day > 1 ? 'shadow-[inset_1px_0_0_#d0c4b0,inset_0_-1px_0_#d0c4b0]' : 'shadow-[inset_0_-1px_0_#d0c4b0]'} text-center font-bold w-8 p-1 ${tatil ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                    {/* Soluklaştırma İÇERİKTE: hücrenin kendisine opacity verilirse
                        arka planı da saydamlaşır, altından kayan satırlar görünür */}
                    <div className={ileriGunMu(day) ? 'opacity-40' : ''}>
                      <div className="leading-tight">{day}</div>
                      <div className="text-[8px] font-normal opacity-70">{HAFTA[hg]}</div>
                    </div>
                  </TableHead>
                );
              })}
              {/* Özet, Net Maaş genişliği kadar içeride durur (bkz. netBaslikRef) */}
              <TableHead className="sticky right-[var(--net-maas-w,6rem)] top-0 bg-[#e8dfd0] z-30 text-center text-[#1a1916] font-bold border-l border-[#d0c4b0] w-16 shadow-[inset_0_-1px_0_#d0c4b0]">
                Özet
              </TableHead>
              <TableHead ref={netBaslikRef} className="sticky right-0 top-0 bg-[#e8dfd0] z-30 text-right text-[#1a1916] font-bold w-24 shadow-[inset_0_-1px_0_#d0c4b0]">
                Net Maaş
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{liste.map(personelSatiri)}</TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="timesheet-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">Puantaj</h1>
          <p className="text-sm text-[#8a7e70]">Personel çalışma takibi ve maaş hesaplama</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Görünüm anahtarı — ikisi de aynı veriye yazar */}
          <div className="flex rounded-lg border border-[#d0c4b0] overflow-hidden bg-white">
            {[
              { deger: 'ay', etiket: 'Aylık Izgara', icon: LayoutGrid },
              { deger: 'hafta', etiket: 'Haftalık', icon: CalendarRange },
              { deger: 'gun', etiket: 'Günlük Giriş', icon: CalendarDays },
            ].map(g => {
              const Icon = g.icon;
              return (
                <button key={g.deger} type="button" onClick={() => setGorunum(g.deger)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors
                    ${gorunum === g.deger ? 'bg-[#3d6e4e] text-white' : 'text-[#8a7e70] hover:bg-[#f5f0e8]'}`}
                  data-testid={`puantaj-gorunum-${g.deger}`}>
                  <Icon size={14} /> {g.etiket}
                </button>
              );
            })}
          </div>

          {gorunum === 'ay' && (
            <>
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-[130px] bg-white border-[#d0c4b0]" data-testid="month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AYLAR.map((m, i) => (
                    <SelectItem key={i} value={(i + 1).toString()}
                      disabled={ayIleride(year, i + 1)}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={year.toString()} onValueChange={(v) => yilDegistir(parseInt(v))}>
                <SelectTrigger className="w-[100px] bg-white border-[#d0c4b0]" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yillar.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={departmanId} onValueChange={setDepartmanId}>
                <SelectTrigger className="w-[190px] bg-white border-[#d0c4b0]" data-testid="departman-filtre">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hepsi">Tüm departmanlar</SelectItem>
                  {departmanlar.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                  <SelectItem value="yok">Departmanı olmayanlar</SelectItem>
                </SelectContent>
              </Select>
              {!isApproved && (
                <Button variant="outline" size="sm" className="border-[#d0c4b0]"
                  onClick={() => { setTemizleAralik({ bas: 1, bit: daysInMonth }); setTemizlePencere(true); }}
                  data-testid="girisleri-temizle">
                  <Eraser size={14} className="mr-1.5" /> Girişleri Temizle
                </Button>
              )}
              {isApproved && (
                <Badge className="bg-green-100 text-green-800 border-green-300">
                  <Lock size={12} className="mr-1" /> Onaylandı
                </Badge>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={temizlePencere} onOpenChange={(v) => !v && setTemizlePencere(false)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Girişleri Temizle</DialogTitle>
            <DialogDescription className="text-xs text-[#8a7e70]">
              Seçili gün aralığındaki girişler silinir ve o günler <b>girilmemiş</b> hale döner.
              Bağlantıdan girilen bir günün kilidi böyle açılır: sorumlu o günü yeniden girebilir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#3F3E39]">
              Kapsam: <b>{departmanId === 'hepsi' ? 'Tüm departmanlar'
                : departmanId === 'yok' ? 'Departmanı olmayanlar'
                : (departmanlar.find(d => d.id === departmanId)?.name || '')}</b>
              {' · '}{aylikPersonel.length + gunlukPersonel.length} personel
            </p>
            <div className="flex items-end gap-2">
              <label className="text-xs text-[#8a7e70]">Başlangıç günü
                <input type="number" min="1" max={daysInMonth} value={temizleAralik.bas}
                  onChange={(e) => setTemizleAralik(a => ({ ...a, bas: e.target.value }))}
                  className="w-full h-9 rounded-md border border-[#d0c4b0] bg-white px-2 text-sm mt-0.5"
                  data-testid="temizle-bas" />
              </label>
              <label className="text-xs text-[#8a7e70]">Bitiş günü
                <input type="number" min="1" max={daysInMonth} value={temizleAralik.bit}
                  onChange={(e) => setTemizleAralik(a => ({ ...a, bit: e.target.value }))}
                  className="w-full h-9 rounded-md border border-[#d0c4b0] bg-white px-2 text-sm mt-0.5"
                  data-testid="temizle-bit" />
              </label>
            </div>
            <p className="text-xs text-[#7a6a2e] bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 rounded-md px-3 py-2">
              <b>Dikkat:</b> girilmemiş gün tahakkukta <b>çalışıldı</b> sayılır. Temizlediğiniz
              günler tahakkuktan önce yeniden doldurulmalı.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemizlePencere(false)} className="border-[#d0c4b0]">
              Vazgeç
            </Button>
            <Button onClick={girisleriTemizle} disabled={temizliyor}
              className="bg-[#a34b3f] text-white hover:bg-[#8a3f35]" data-testid="temizle-onayla">
              {temizliyor && <Loader2 size={15} className="mr-1.5 animate-spin" />}Temizle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {gorunum === 'gun' ? (
        <GunlukGiris ayar={ayar} onKaydedildi={fetchTimesheet} />
      ) : gorunum === 'hafta' ? (
        <HaftalikGorunum ayar={ayar} onKaydedildi={fetchTimesheet} />
      ) : loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>
      ) : (
        <>
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-4">
                <span className="text-sm font-medium text-[#8a7e70]">Durum Açıklamaları:</span>
                {DURUMLAR.map(d => (
                  <div key={d.deger} className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold ${d.sinif}`}>
                      {d.kod}
                    </div>
                    <span className="text-sm text-[#1a1916]">{d.etiket}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#8a7e70]">
                Bir güne tıklayınca durumu ve o güne ait ekstra mesai saatini girebilirsiniz.
                Haftalık izin 1 gün çalışılmış sayılır; resmi tatil çalışması {ayar.resmi_tatil_katsayi.toLocaleString('tr-TR')} gün,
                fazla mesai {ayar.mesai_katsayi.toLocaleString('tr-TR')} kat sayılır
                (saatlik ücret = günlük ücret ÷ {ayar.gunluk_calisma_saati.toLocaleString('tr-TR')}).
                <b>Tüm fazla mesai ücrete yansır</b>; fiilen izin kullandırılırsa o gün
                Ücretsiz İzin olarak işaretlenir. Katsayılar Admin Paneli → Puantaj Ayarları'ndan değiştirilir.
              </p>
            </CardContent>
          </Card>

          {izgara('Aylık Personeller', aylikPersonel)}
          {izgara('Günlük / Part-time Personeller', gunlukPersonel)}

          {employees.length === 0 && (
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-12 text-center">
                <Users size={48} className="mx-auto mb-4 text-[#8a7e70] opacity-50" />
                <p className="text-[#8a7e70]">Aktif personel bulunamadı</p>
              </CardContent>
            </Card>
          )}

          {employees.length > 0 && (
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <p className="text-sm text-[#8a7e70]">Toplam Kesinti</p>
                      <p className="text-xl font-bold text-red-600">{pbToplam(toplam.kesinti)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#8a7e70]">Fazla Mesai</p>
                      <p className="text-xl font-bold text-[#1a1916]">
                        {toplam.mesaiSaat.toLocaleString('tr-TR')} sa
                        <span className="text-sm font-normal text-[#8a7e70]"> · {pbToplam(toplam.mesaiTutar)}</span>
                      </p>
                    </div>
                    {toplam.eksikSaat > 0 && (
                      <div>
                        <p className="text-sm text-[#8a7e70]">Eksik Çalışma</p>
                        <p className="text-xl font-bold text-[#a34b3f]">
                          {toplam.eksikSaat.toLocaleString('tr-TR')} sa
                          <span className="text-sm font-normal text-[#8a7e70]"> · −{pbToplam(toplam.eksikTutar)}</span>
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-[#8a7e70]">Resmi Tatil Çalışması</p>
                      <p className="text-xl font-bold text-[#1a1916]">{toplam.rtc} gün</p>
                    </div>
                    {Object.keys(toplam.haklar).length > 0 && (
                      <div>
                        <p className="text-sm text-[#8a7e70]">İlave Haklar</p>
                        <p className="text-xl font-bold text-[#2e6fa0]">{pbToplam(toplam.haklar)}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-[#8a7e70]">Toplam Net Maaş</p>
                      <p className="text-xl font-bold text-[#3d6e4e]">{pbToplam(toplam.net)}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    {!isApproved ? (
                      <>
                        <Button variant="outline" onClick={kaydet} disabled={saving}
                          className="border-[#d0c4b0]" data-testid="save-timesheet-btn">
                          {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                          Kaydet
                        </Button>
                        {tahakkukYetkisi && (
                          <Button onClick={onayla} disabled={approving}
                            className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="approve-timesheet-btn">
                            {approving ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle size={16} className="mr-2" />}
                            Tahakkuku Onayla
                          </Button>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle size={20} />
                        <span className="font-medium">Bu ay için puantaj onaylanmış</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* GEÇİCİ GÖREV — personel ay içinde başka bir sorumluluk merkezinde
          çalıştıysa. Aralık olarak giriliyor; maaş tahakkuku sunucuda bu gün
          dağılımına göre merkezlere bölünüyor (hr.py -> _maas_merkez_paylari). */}
      <GecidiGorevDialog
        emp={gorevDialog}
        acik={!!gorevDialog}
        kapat={() => setGorevDialog(null)}
        katalog={merkezKatalogu}
        daysInMonth={daysInMonth}
        sonGun={Math.min(daysInMonth, sonGirisGunu)}
        ozet={gorevDialog ? merkezOzeti(gorevDialog) : null}
        onYaz={gorevYaz}
        onTemizle={gorevTemizle}
        ay={AYLAR[month - 1]}
      />
    </div>
  );
}


// ─── GÜNLÜK GİRİŞ ───
// Aylık ızgara ile aynı puantaj dokümanına yazar; ayrı bir kayıt tutmuyoruz.
function GunlukGiris({ ayar, onKaydedildi }) {
  const [tarih, setTarih] = useState(bugun());
  const [kayitlar, setKayitlar] = useState([]);
  const [onayli, setOnayli] = useState(false);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  useEffect(() => {
    let iptal = false;
    setYukleniyor(true);
    api.get(`/hr/timesheet/gun?tarih=${tarih}`)
      .then(r => {
        if (iptal) return;
        setKayitlar(r.data.kayitlar || []);
        setOnayli(r.data.is_approved || false);
      })
      .catch(() => { if (!iptal) toast.error('Gün yüklenemedi'); })
      .finally(() => { if (!iptal) setYukleniyor(false); });
    return () => { iptal = true; };
  }, [tarih]);

  const guncelle = (empId, alan, deger) => {
    if (onayli) return;
    setKayitlar(p => p.map(k => k.employee_id === empId ? { ...k, [alan]: deger } : k));
  };

  const gunKaydir = (adim) => {
    const d = new Date(`${tarih}T00:00:00`);
    d.setDate(d.getDate() + adim);
    setTarih(d.toLocaleDateString('en-CA'));
  };

  const hepsineUygula = (durum) => {
    if (onayli) return;
    setKayitlar(p => p.map(k => ({ ...k, status: durum })));
  };

  const kaydet = async () => {
    setKaydediliyor(true);
    try {
      await api.post('/hr/timesheet/gun', {
        tarih,
        kayitlar: kayitlar.map(k => ({
          employee_id: k.employee_id, status: k.status,
          mesai_saat: sayi(k.mesai_saat),
        })),
      });
      toast.success(`${tarih} günü kaydedildi`);
      onKaydedildi?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
    } finally {
      setKaydediliyor(false);
    }
  };

  const ozet = useMemo(() => {
    const s = { calisan: 0, izinli: 0, yok: 0, rtc: 0, mesai: 0 };
    kayitlar.forEach(k => {
      if (['worked', 'weekly_leave', 'public_holiday_worked'].includes(k.status)) s.calisan++;
      else if (['annual_leave', 'public_holiday'].includes(k.status)) s.izinli++;
      else s.yok++;
      if (k.status === 'public_holiday_worked') s.rtc++;
      s.mesai += Math.max(0, sayi(k.mesai_saat));
    });
    return s;
  }, [kayitlar]);

  const g = new Date(`${tarih}T00:00:00`);
  const gunAdi = Number.isNaN(g.getTime()) ? '' : `${g.getDate()} ${AYLAR[g.getMonth()]} ${g.getFullYear()}, ${HAFTA[g.getDay()]}`;

  return (
    <div className="space-y-4" data-testid="puantaj-gunluk">
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => gunKaydir(-1)}
              className="border-[#d0c4b0] h-9 px-2" title="Önceki gün"
              data-testid="puantaj-gun-onceki">
              <ChevronLeft size={16} />
            </Button>
            {/* `max` + devre dışı ileri düğmesi: bugünden sonrasına geçilemez
                (bkz. dosya başı, GİRİŞ sınırı). Elle yazılan ileri tarih de
                `onChange`de bugüne çevriliyor — `max` yalnızca takvim seçicisini
                sınırlar, klavyeyle yazmayı engellemez. */}
            <Input type="date" value={tarih} max={sonSecilebilirGun()}
              onChange={(e) => {
                const v = e.target.value || bugun();
                setTarih(gunIleride(v) ? sonSecilebilirGun() : v);
              }}
              className="w-44 bg-white border-[#d0c4b0]" data-testid="puantaj-gun-tarih" />
            <Button variant="outline" size="sm" onClick={() => gunKaydir(1)}
              disabled={gunIleride(ileriGun(tarih, 1))}
              className="border-[#d0c4b0] h-9 px-2" title="Sonraki gün"
              data-testid="puantaj-gun-sonraki">
              <ChevronRight size={16} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTarih(bugun())}
              className="border-[#d0c4b0] h-9">Bugün</Button>
            <div className="ml-1">
              <p className="text-sm font-semibold text-[#1a1916]">{gunAdi}</p>
              <p className="text-xs text-[#8a7e70]">
                {kayitlar.length} personel · {ozet.calisan} çalıştı · {ozet.izinli} izinli · {ozet.yok} yok
                {ozet.rtc > 0 && ` · ${ozet.rtc} RTÇ`}
                {ozet.mesai > 0 && ` · ${ozet.mesai.toLocaleString('tr-TR')} sa mesai`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!onayli && kayitlar.length > 0 && (
              <Select onValueChange={hepsineUygula}>
                <SelectTrigger className="w-[190px] bg-white border-[#d0c4b0]" data-testid="puantaj-gun-hepsi">
                  <SelectValue placeholder="Hepsine uygula…" />
                </SelectTrigger>
                <SelectContent>
                  {DURUMLAR.map(d => <SelectItem key={d.deger} value={d.deger}>{d.etiket}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {onayli ? (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <Lock size={12} className="mr-1" /> Bu ay onaylandı, değiştirilemez
              </Badge>
            ) : (
              <Button onClick={kaydet} disabled={kaydediliyor || !kayitlar.length}
                className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]" data-testid="puantaj-gun-kaydet">
                {kaydediliyor ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Günü Kaydet
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {kayitlar.length > 0 && !yukleniyor && (
        <div className="hidden md:flex items-center gap-4 px-4 text-xs font-semibold
                        uppercase tracking-wide text-[#8a7e70]">
          <span className="w-56">Personel</span>
          <span className="flex-1">Durum</span>
          <span className="w-24">Ekstra Mesai</span>
          <span className="w-[150px]">Karşılığı</span>
        </div>
      )}

      {yukleniyor ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>
      ) : kayitlar.length === 0 ? (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-[#8a7e70] opacity-50" />
            <p className="text-[#8a7e70]">Bu tarihte kadroda personel yok</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-0 divide-y divide-[#d0c4b0]">
            {kayitlar.map(k => (
              <div key={k.employee_id}
                className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 py-3 hover:bg-[#f5f0e8]">
                <div className="md:w-56 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-[#1a1916]">{k.full_name}</span>
                    {!k.is_active && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#7a3535]/15 text-[#7a3535]">Pasif</span>
                    )}
                  </div>
                  <p className="text-xs text-[#8a7e70]">
                    {k.employment_type === 'full_time' ? 'Aylık' : 'Günlük'}
                  </p>
                  {k.tahakkuklu && (
                    <p className="text-[10px] text-[#8a7e70] inline-flex items-center gap-0.5 mt-0.5">
                      <Lock size={9} /> Tahakkuk edildi — kilit Aylık Izgara'da
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-1 flex-1">
                  {/* Tahakkuk edilmis personel buradan da degistirilemez;
                      kilit tek sekmeye ozel olsaydi obur sekme onu delerdi.
                      Acma yeri Aylik Izgara — tahakkuk dugmesi orada. */}
                  {DURUMLAR.map(d => (
                    <button key={d.deger} type="button" disabled={onayli || k.tahakkuklu}
                      onClick={() => guncelle(k.employee_id, 'status', d.deger)}
                      title={k.tahakkuklu
                        ? 'Tahakkuk edildi — kilidi Aylık Izgara görünümünden açın'
                        : d.etiket}
                      className={`px-2 h-7 rounded text-[11px] font-bold border transition-colors
                        ${k.status === d.deger
                          ? `${d.sinif} text-white border-transparent`
                          : 'bg-white text-[#8a7e70] border-[#d0c4b0] hover:bg-[#f5f0e8]'}
                        ${onayli || k.tahakkuklu ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`puantaj-gun-${k.employee_id}-${d.deger}`}>
                      {d.kod}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label className="text-xs text-[#8a7e70] md:hidden">Ekstra mesai (saat)</label>
                  <Input type="number" step="0.5" disabled={onayli || k.tahakkuklu}
                    value={k.mesai_saat || ''} placeholder="0"
                    onChange={(e) => guncelle(k.employee_id, 'mesai_saat', e.target.value)}
                    className={`h-8 w-24 bg-white border-[#d0c4b0] ${k.tahakkuklu ? 'opacity-40' : ''}`}
                    data-testid={`puantaj-gun-mesai-${k.employee_id}`} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-[#8a7e70]">
        Günlük giriş, aylık ızgara ile aynı puantaja yazılır — kaydettiğiniz gün
        Aylık Izgara görünümünde de görünür. Fazla mesai {ayar.mesai_katsayi.toLocaleString('tr-TR')} kat,
        resmi tatil çalışması {ayar.resmi_tatil_katsayi.toLocaleString('tr-TR')} gün üzerinden hesaplanır.
      </p>
    </div>
  );
}


// ─── HAFTALIK GÖRÜNÜM ───
// Hafta PAZARTESİ başlar, PAZAR biter. Hafta iki aya taşabildiği için veriyi
// sunucu ilgili ay dokümanlarından toplayıp dağıtıyor; onaylanmış aya düşen
// günler kilitli gelir ve kaydedilmez.
function HaftalikGorunum({ ayar, onKaydedildi }) {
  const [tarih, setTarih] = useState(bugun());
  const [gunler, setGunler] = useState([]);
  const [kayitlar, setKayitlar] = useState([]);
  const [aylar, setAylar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const r = await api.get(`/hr/timesheet/hafta?tarih=${tarih}`);
      setGunler(r.data.gunler || []);
      setKayitlar(r.data.kayitlar || []);
      setAylar(r.data.aylar || []);
    } catch {
      toast.error('Hafta yüklenemedi');
    } finally {
      setYukleniyor(false);
    }
  }, [tarih]);

  useEffect(() => { yukle(); }, [yukle]);

  const kaydir = (haftaSayisi) => {
    const d = new Date(`${tarih}T00:00:00`);
    d.setDate(d.getDate() + haftaSayisi * 7);
    setTarih(d.toLocaleDateString('en-CA'));
  };

  const hucreDegistir = (empId, gunTarih, alan, deger) => {
    setKayitlar(p => p.map(k => k.employee_id !== empId ? k : {
      ...k,
      gunler: { ...k.gunler, [gunTarih]: { ...k.gunler[gunTarih], [alan]: deger } },
    }));
  };

  const kaydet = async () => {
    // Kilitli ay, kadro dışı ve ileri tarihli günler gönderilmez. İleri gün
    // ekranda kilitli olsa da gönderilseydi, hiç girilmemiş gün kaydedilince
    // "girilmiş" hale gelirdi (bkz. dosya başı, GİRİŞ sınırı).
    const gonder = [];
    kayitlar.forEach(k => gunler.forEach(g => {
      const h = k.gunler[g.tarih];
      if (!g.kilitli && h?.kadroda && !gunIleride(g.tarih)) {
        gonder.push({
          employee_id: k.employee_id, tarih: g.tarih, status: h.status,
          mesai_saat: sayi(h.mesai_saat),
        });
      }
    }));
    if (!gonder.length) { toast.error('Kaydedilecek gün yok'); return; }
    setKaydediliyor(true);
    try {
      const r = await api.post('/hr/timesheet/hafta', { kayitlar: gonder });
      const atlanan = r.data?.atlanan_aylar || [];
      toast.success(atlanan.length
        ? `Hafta kaydedildi — ${atlanan.join(', ')} onaylı olduğu için atlandı`
        : 'Hafta kaydedildi');
      onKaydedildi?.();
      await yukle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
    } finally {
      setKaydediliyor(false);
    }
  };

  // Haftalık özet: çalışılan gün / RTÇ / mesai saati
  const ozet = (k) => gunler.reduce((a, g) => {
    const h = k.gunler[g.tarih];
    if (!h?.kadroda) return a;
    if (['worked', 'weekly_leave', 'public_holiday_worked'].includes(h.status)) a.calisilan++;
    if (h.status === 'public_holiday_worked') a.rtc++;
    a.mesai += Math.max(0, sayi(h.mesai_saat));
    return a;
  }, { calisilan: 0, rtc: 0, mesai: 0 });

  const bicimGun = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return `${d.getDate()} ${AYLAR[d.getMonth()].slice(0, 3)}`;
  };
  const araligiYaz = () => {
    if (gunler.length < 7) return '';
    const b = new Date(`${gunler[0].tarih}T00:00:00`);
    const so = new Date(`${gunler[6].tarih}T00:00:00`);
    return `${b.getDate()} ${AYLAR[b.getMonth()]} – ${so.getDate()} ${AYLAR[so.getMonth()]} ${so.getFullYear()}`;
  };
  const kilitliAy = aylar.filter(a => a.onayli);

  return (
    <div className="space-y-4" data-testid="puantaj-haftalik">
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => kaydir(-1)}
              className="border-[#d0c4b0] h-9 px-2" data-testid="puantaj-hafta-onceki">
              <ChevronLeft size={16} />
            </Button>
            {/* Haftada gezinme sınırı HAFTANIN BAŞINA (pazartesi) bakar: bu hafta
                her zaman açılabilmeli, içindeki ileri günler kilitli görünür.
                Eskiden `tarih + 7` kontrol ediliyordu; geçen cuma seçiliyken bugün
                çarşambaysa "bu hafta"ya ileri düğmesiyle geçilemiyordu. */}
            <Input type="date" value={tarih} max={sonSecilebilirGun()}
              onChange={(e) => {
                const v = e.target.value || bugun();
                setTarih(gunIleride(v) ? sonSecilebilirGun() : v);
              }}
              className="w-44 bg-white border-[#d0c4b0]" data-testid="puantaj-hafta-tarih" />
            <Button variant="outline" size="sm" onClick={() => kaydir(1)}
              disabled={gunIleride(haftaBasi(ileriGun(tarih, 7)))}
              className="border-[#d0c4b0] h-9 px-2" data-testid="puantaj-hafta-sonraki">
              <ChevronRight size={16} />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTarih(bugun())}
              className="border-[#d0c4b0] h-9">Bu hafta</Button>
            <div className="ml-2">
              <p className="text-sm font-semibold text-[#1a1916]">{araligiYaz()}</p>
              <p className="text-xs text-[#8a7e70]">{kayitlar.length} personel · Pazartesi–Pazar</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {kilitliAy.length > 0 && (
              <Badge className="bg-green-100 text-green-800 border-green-300">
                <Lock size={12} className="mr-1" />
                {kilitliAy.map(a => `${a.year}/${String(a.month).padStart(2, '0')}`).join(', ')} onaylı
              </Badge>
            )}
            <Button onClick={kaydet} disabled={kaydediliyor || !kayitlar.length}
              className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e]" data-testid="puantaj-hafta-kaydet">
              {kaydediliyor ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Haftayı Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>

      {yukleniyor ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={32} /></div>
      ) : kayitlar.length === 0 ? (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-12 text-center">
            <Users size={48} className="mx-auto mb-4 text-[#8a7e70] opacity-50" />
            <p className="text-[#8a7e70]">Bu haftada kadroda personel yok</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-0 overflow-x-auto">
            {/* Aylik izgaradaki ile ayni: baslik tablonun kendi kaydirma kutusuna yapisir */}
            <Table containerClassName="max-h-[max(20rem,calc(100vh-8rem))]">
              <TableHeader>
                <TableRow className="bg-[#e8dfd0]">
                  <TableHead className="sticky left-0 top-0 bg-[#e8dfd0] z-30 min-w-[120px] md:min-w-[180px] text-[#1a1916] font-bold border-r border-[#d0c4b0] shadow-[inset_0_-1px_0_#d0c4b0]">
                    Personel
                  </TableHead>
                  {gunler.map((g, gi) => {
                    const haftaSonu = g.gun_adi === 'Cmt' || g.gun_adi === 'Paz';
                    const ileri = gunIleride(g.tarih);
                    return (
                      <TableHead key={g.tarih}
                        title={ileri ? 'İleri tarih — henüz girilemez' : undefined}
                        className={`sticky top-0 z-20 bg-[#e8dfd0] ${gi > 0 ? 'shadow-[inset_1px_0_0_#d0c4b0,inset_0_-1px_0_#d0c4b0]' : 'shadow-[inset_0_-1px_0_#d0c4b0]'} text-center font-bold w-16 p-1 ${haftaSonu ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                        {/* Soluklaştırma içerikte (aylık ızgaradaki notla aynı sebep) */}
                        <div className={ileri ? 'opacity-40' : ''}>
                          <div className="leading-tight">{g.gun_adi}</div>
                          <div className="text-[10px] font-normal opacity-70">{bicimGun(g.tarih)}</div>
                          {g.kilitli && <Lock size={9} className="mx-auto mt-0.5 opacity-60" />}
                        </div>
                      </TableHead>
                    );
                  })}
                  <TableHead className="sticky top-0 z-20 bg-[#e8dfd0] shadow-[inset_0_-1px_0_#d0c4b0] text-center text-[#1a1916] font-bold border-l border-[#d0c4b0] w-24">
                    Hafta Özeti
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kayitlar.map(k => {
                  const o = ozet(k);
                  return (
                    <TableRow key={k.employee_id} className="hover:bg-[#f5f0e8]">
                      <TableCell className="sticky left-0 bg-[#faf7f2] z-10 min-w-[120px] md:min-w-[180px] border-r border-[#d0c4b0]">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-[#1a1916]">{k.full_name}</span>
                          {!k.is_active && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-[#7a3535]/15 text-[#7a3535]">Pasif</span>
                          )}
                        </div>
                        <p className="text-xs text-[#8a7e70]">
                          {k.employment_type === 'full_time' ? 'Aylık' : 'Günlük'}
                        </p>
                      </TableCell>
                      {gunler.map((g, gi) => {
                        const h = k.gunler[g.tarih] || {};
                        const ileri = gunIleride(g.tarih);
                        return (
                          <TableCell key={g.tarih} className={`p-1 ${gi > 0 ? 'shadow-[inset_1px_0_0_#e3d8c6]' : ''}`}>
                            <div className="flex justify-center">
                              <DurumHucresi
                                durum={h.kadroda ? (h.status || 'worked') : 'absent'}
                                saat={sayi(h.mesai_saat)}
                                kilitli={g.kilitli || !h.kadroda || h.tahakkuklu || ileri}
                                soluk={!h.kadroda || h.tahakkuklu || ileri}
                                baslik={`${k.full_name} · ${bicimGun(g.tarih)} ${g.gun_adi}`
                                  + (h.tahakkuklu ? ' · tahakkuk edildi, kilidi Aylık Izgara görünümünden açın' : '')}
                                onDurum={(d) => hucreDegistir(k.employee_id, g.tarih, 'status', d)}
                                onMesai={(v) => hucreDegistir(k.employee_id, g.tarih, 'mesai_saat', v)}
                                testId={`puantaj-hafta-${k.employee_id}-${g.tarih}`}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                      <TableCell className="border-l border-[#d0c4b0] text-center">
                        <div className="text-xs space-y-0.5 whitespace-nowrap">
                          <p><span className="text-[#8a7e70]">Ç:</span> <span className="font-semibold">{o.calisilan}</span></p>
                          {o.rtc > 0 && (
                            <p><span className="text-[#8a7e70]">RTÇ:</span> <span className="font-semibold text-cyan-800">{o.rtc}</span></p>
                          )}
                          {o.mesai > 0 && (
                            <p><span className="text-[#8a7e70]">FM:</span> <span className="font-semibold">{o.mesai.toLocaleString('tr-TR')} sa</span></p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {DURUMLAR.map(d => (
          <div key={d.deger} className="flex items-center gap-1.5">
            <div className={`w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold ${d.sinif}`}>
              {d.kod}
            </div>
            <span className="text-xs text-[#1a1916]">{d.etiket}</span>
          </div>
        ))}
      </div>

      <p className="text-xs text-[#8a7e70]">
        Haftalar Pazartesi başlar, Pazar biter. Bir güne tıklayıp durumu ve o güne ait
        ekstra mesai saatini girebilirsiniz. Hafta iki aya taşıyorsa her gün kendi ayının
        puantajına yazılır; onaylanmış aya düşen günler kilitlidir.
        Fazla mesai {ayar.mesai_katsayi.toLocaleString('tr-TR')} kat,
        resmi tatil çalışması {ayar.resmi_tatil_katsayi.toLocaleString('tr-TR')} gün sayılır.
      </p>

    </div>
  );
}


// Geçici görev aralığı. Ayrı bileşen: puantaj tablosu zaten ağır, diyalogun
// kendi state'i her tuşta tabloyu yeniden çizmesin.
function GecidiGorevDialog({ emp, acik, kapat, katalog, daysInMonth, sonGun, ozet,
                             onYaz, onTemizle, ay }) {
  const [merkezId, setMerkezId] = useState('');
  const [baslangic, setBaslangic] = useState('1');
  const [bitis, setBitis] = useState(String(sonGun));

  useEffect(() => {
    if (acik) { setMerkezId(''); setBaslangic('1'); setBitis(String(sonGun)); }
  }, [acik, sonGun]);

  if (!emp) return null;

  const uygula = () => {
    if (!merkezId) return;
    onYaz(emp.id, merkezId, baslangic, bitis);
    kapat();
  };

  return (
    <Dialog open={acik} onOpenChange={(v) => !v && kapat()}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold">Geçici Görev — {emp.full_name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8a7e70]">
            Personelin asıl merkezi <b>{ozet?.asilAd || 'atanmamış'}</b>. Ay içinde başka bir
            merkezde çalıştığı günleri buradan işaretleyin; maaş tahakkuku gün sayısına
            göre merkezlere bölünür.
          </DialogDescription>
        </DialogHeader>

        {ozet?.gecici?.length > 0 && (
          <div className="text-xs text-[#2e6fa0] bg-[#2e6fa0]/10 rounded px-3 py-2">
            Tanımlı geçici görev: {ozet.gecici.map(g => `${g.gun} gün ${g.ad}`).join(', ')}
          </div>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs text-[#8a7e70]">Görev Yapılan Merkez</Label>
            <select value={merkezId} onChange={(e) => setMerkezId(e.target.value)}
              className="w-full h-9 px-3 rounded-md border border-[#d0c4b0] bg-white text-sm"
              data-testid="gecici-gorev-merkez">
              <option value="">Seçiniz</option>
              {katalog.filter(m => m.id !== emp.cost_center_id).map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Başlangıç Günü</Label>
              <Input type="number" min="1" max={sonGun} value={baslangic}
                onChange={(e) => setBaslangic(e.target.value)}
                className="bg-white border-[#d0c4b0] h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#8a7e70]">Bitiş Günü</Label>
              <Input type="number" min="1" max={sonGun} value={bitis}
                onChange={(e) => setBitis(e.target.value)}
                className="bg-white border-[#d0c4b0] h-9" />
            </div>
          </div>
          <p className="text-[11px] text-[#8a7e70]">
            {ay} ayı {daysInMonth} gün.
            {sonGun < daysInMonth && ` Bugünden sonrası (${sonGun + 1}–${daysInMonth}) henüz girilemez.`}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onTemizle(emp.id); kapat(); }}
            className="border-[#d0c4b0] text-[#a34b3f]">
            Geçici görevleri kaldır
          </Button>
          <Button onClick={uygula} disabled={!merkezId}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"
            data-testid="gecici-gorev-uygula">
            Uygula
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
